import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Save } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ViewModeSelector } from '../Pipelines/PipelineControls';
import ModuleKanban from '../Pipelines/ModuleKanban';
import { SectionName, Tab, TabsDiv } from '../../../components/styles/subheader-style';

import { useTenant } from '../../../contexts/TenantContext';
import { OrderActionsProvider, useOrderActions } from '../../../contexts/OrderActionsContext';
import { usePersistedFilters } from '../../../hooks/usePersistedFilters';
import { PageHeader } from '../../../components/StandardPage';
import { Header } from '../../../components/SubHeaderFilter/types';
import ConfirmationModal from '../../../components/ModalConfirmation';
import { Content } from './style';
import { Button } from './AddProduct/style';
import stock from '../../../assets/stock/TableMovementHeader/list-active.svg';
import resource from '../../../assets/home/resource.svg';
import steel from '../../../assets/home/steel.svg';
import compositionIcon from '../../../assets/home/length.svg';
import funnelIcon from '../../../assets/home/production.svg';

const productFinalTableHeaders: Header[] = [
  { key: 'name', label: 'Produto', type: 'string' },
  { key: 'productCode', label: 'Código', type: 'string' },
  { key: 'quantity', label: 'Quantidade', type: 'number' },
  { key: 'norm', label: 'Norma', type: 'string' },
  { key: 'ncm', label: 'Ncm', type: 'string' },
  { key: 'format', label: 'Formato', type: 'string' },
  { key: 'unit', label: 'Unidade', type: 'string' },
  { key: 'scriptName', label: 'Script', type: 'string' },
  { key: 'items', label: 'Itens', type: 'string' },
  { key: 'deadline', label: 'Prazo (dias)', type: 'number' },
  { key: 'cost', label: 'Custo (últ.)', type: 'currency' },
  { key: 'price', label: 'Preço (últ)', type: 'currency' },
];

const getTabs = (isFashionTenant: boolean) => {
  if (isFashionTenant) {
    return [
      { path: '/panel/stock', image: stock, name: 'Tecidos' },
      { path: '/panel/stock/resource', image: resource, name: 'Aviamentos' },
      { path: '/panel/product-final', image: steel, name: 'Produto engenheirado' }
    ];
  }

  return [
    { path: '/panel/product-final', image: steel, name: 'Ficha técnica de produto' },
    { path: '/panel/product-final/funnel', image: funnelIcon, name: 'Funil' },
  ];
};

const getActiveIndex = (pathname: string, isFashionTenant: boolean) => {
  if (isFashionTenant) {
    if (pathname.includes('/panel/resource')) return 1;
    if (pathname.includes('/panel/product-final')) return 2;
    if (pathname.includes('/panel/stock')) return 0;
    return 0;
  }
  if (pathname.includes('/panel/product-final/funnel')) return 1;
  return 0;
};

const ProductFinalsLayoutContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerSaveOrder } = useOrderActions();
  const queryClient = useQueryClient();

  const [refresh, setRefresh] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [viewMode, setViewMode] = useState<'board' | 'table'>('table');

  const filtersState = usePersistedFilters('product_final');

  const isCreatePage = location.pathname.includes('/panel/product-final/create');
  const isEditPage = location.pathname.includes('/panel/product-final/edit');
  const isFormPage = isCreatePage || isEditPage;
  const isFunnelPage = location.pathname.includes('/panel/product-final/funnel');
  const newProductId = location.state?.newProductId;
  const outletContext = useOutletContext<{ search?: string }>();
  const search = outletContext?.search ?? '';

  const { isFashionTenant } = useTenant();
  const tabItems = getTabs(isFashionTenant);
  const tabIndexActived = getActiveIndex(location.pathname, isFashionTenant);

  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const handleCancel = () => {
    setShowCancelModal(false);
    navigate(returnTo || '/panel/product-final');
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['products'] });
    setRefresh(prev => !prev);
  };

  const headerTitle = isFormPage
    ? (isEditPage ? 'Editar Produto' : `Cadastrar produto engenheirado ${newProductId || ''}`)
    : (isFashionTenant ? 'Estoque' : 'Desenvolvimento de Produto');

  return (
    <>
      {!isFormPage && !isFunnelPage && (
        <PageHeader
          title={headerTitle}
          tableName="Product"
          moduleName="Produtos Engenheirados"
          tabs={tabItems.length > 0 ? tabItems.map((tab, index) => ({ key: String(index), label: tab.name, icon: tab.image })) : undefined}
          activeTab={String(tabIndexActived)}
          onTabChange={(key) => {
            const index = Number(key);
            if (isFashionTenant) {
              if (index === 0) {
                navigate('/panel/stock');
              } else if (index === 1) {
                navigate('/panel/stock/resource');
              } else if (index === 2) {
                navigate('/panel/product-final');
              }
            } else {
              if (index === 0) {
                navigate('/panel/product-final');
              } else if (index === 1) {
                navigate('/panel/product-final/funnel');
              }
            }
          }}
          onReload={handleRefresh}
          filterHeaders={productFinalTableHeaders}
          appliedFilters={filtersState.appliedFilters}
          onApplyFilter={filtersState.handleApplyFilter}
          onRemoveFilter={filtersState.handleRemoveFilter}
          onClearAllFilters={filtersState.handleClearAll}
          createButton={{ label: 'Cadastrar produto', onClick: () => navigate('/panel/product-final/create') }}
          rightExtra={
            <ViewModeSelector
              value={viewMode}
              onChange={setViewMode}
              boardLabel='Quadro'
              tableLabel='Tabela'
            />
          }
        />
      )}

      {isFormPage && (
        <PageHeader
          title={headerTitle}
          tableName="Product"
          moduleName="Produtos Engenheirados"
          hasTable={false}
          showAuditHeaderMenu={false}
          extraActions={
            <>
              <Button variant='danger-outline' onClick={() => setShowCancelModal(true)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={triggerSaveOrder}>
                <Save size={18} style={{ marginRight: '8px' }} />
                {isEditPage ? 'Salvar Alterações' : 'Salvar Produto'}
              </Button>
            </>
          }
        />
      )}

      <Content>
        <Outlet context={{ refresh, appliedFilters: filtersState.appliedFilters, search, viewMode }} />
      </Content>

      {showCancelModal && (
        <ConfirmationModal
          isOpen={showCancelModal}
          title={isEditPage ? "Cancelar Edição de Produto" : "Cancelar Criação de Produto"}
          message="Você tem certeza? Todas as alterações não salvas serão perdidas."
          actions={[
            { label: 'Confirmar', onClick: handleCancel, variant: 'danger' },
            { label: 'Voltar', onClick: () => setShowCancelModal(false), variant: 'outline' }
          ]}
        />
      )}
    </>
  );
};

export const ProductFinalFunnel: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { path: '/panel/product-final', image: steel, name: 'Ficha técnica de produto' },
    { path: '/panel/product-final/funnel', image: funnelIcon, name: 'Funil' },
  ];

  const activeTabIndex = location.pathname.includes('/panel/product-final/funnel') ? 1 : 0;

  return (
    <ModuleKanban
      moduleType="product_development"
      moduleName="Desenvolvimento de Produto"
      subModule="steel"
      headerLeftContent={
        <>
          <SectionName>Desenvolvimento de Produto</SectionName>
          <TabsDiv>
            {tabs.map((tab, index) => (
              <Tab
                key={tab.path}
                $isFirst={index === 0}
                $isLast={index === tabs.length - 1}
                $isActive={index === activeTabIndex}
                onClick={() => {
                  navigate(tab.path);
                }}
              >
                <img src={tab.image} alt={`${tab.name}-icon`} />
                {tab.name}
              </Tab>
            ))}
          </TabsDiv>
        </>
      }
    />
  );
};

const ProductFinals: React.FC = () => {
  return (
    <OrderActionsProvider>
      <ProductFinalsLayoutContent />
    </OrderActionsProvider>
  );
};

export default ProductFinals;
